import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('jabari_user'));
  } catch {
    return null;
  }
};

const getStoredSelectedBranch = () => {
  try {
    return JSON.parse(localStorage.getItem('jabari_selected_branch'));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [permissions, setPermissions] = useState({});
  const [selectedBranch, setSelectedBranch] = useState(
    getStoredSelectedBranch
  );

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/permissions');
      setPermissions(data);
    } catch {
      setPermissions({});
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadPermissions();
    }
  }, [user, loadPermissions]);

  useEffect(() => {
    if (user && !user.is_super_admin) {
      setSelectedBranch(null);
      localStorage.removeItem('jabari_selected_branch');
    }
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', {
      email,
      password,
    });
    localStorage.setItem('jabari_token', data.token);
    localStorage.setItem('jabari_user', JSON.stringify(data.user));
    setUser(data.user);
    if (!data.user.is_super_admin) {
      setSelectedBranch(null);
      localStorage.removeItem('jabari_selected_branch');
    }
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('jabari_token');
    localStorage.removeItem('jabari_user');
    localStorage.removeItem('jabari_selected_branch');
    setUser(null);
    setPermissions({});
    setSelectedBranch(null);
  };

  const selectBranch = useCallback((branch) => {
    if (!branch?.id) {
      setSelectedBranch(null);
      localStorage.removeItem('jabari_selected_branch');
      return;
    }
    const normalizedBranch = {
      id: branch.id,
      name: branch.name,
    };
    setSelectedBranch(normalizedBranch);
    localStorage.setItem(
      'jabari_selected_branch',
      JSON.stringify(normalizedBranch)
    );
  }, []);

  const can = useCallback(
    (module, action = 'view') => {
      if (user?.is_super_admin) return true;
      return !!permissions[module]?.[action];
    },
    [permissions, user]
  );

  const isSuper = !!user?.is_super_admin;

  const activeBranchId = isSuper
    ? selectedBranch?.id || ''
    : user?.branch_id || '';

  const activeBranchName = isSuper
    ? selectedBranch?.name || 'All branches'
    : user?.branch_name || '';

  // Used by new v2 pages — same logic as activeBranchId but returns null instead of ''
  // so API calls omit the branch filter entirely when super admin is on "All branches"
  const effectiveBranchId = isSuper
    ? selectedBranch?.id || null
    : user?.branch_id || null;

  const value = useMemo(
    () => ({
      user,
      permissions,
      login,
      logout,
      can,
      isSuper,
      // User's assigned branch
      branchId: user?.branch_id,
      branchName: user?.branch_name,
      // Globally selected branch
      activeBranchId,
      activeBranchName,
      selectedBranch,
      selectBranch,
      isAllBranches: isSuper && !activeBranchId,
      // v2 addition — used by new pages
      effectiveBranchId,
    }),
    [
      user,
      permissions,
      can,
      isSuper,
      activeBranchId,
      activeBranchName,
      selectedBranch,
      selectBranch,
      effectiveBranchId,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);